import { CognitoUser } from 'amazon-cognito-identity-js'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'

import { AuthHeading } from '../../components/authentication/auth-heading'
import { Button } from '../../components/button'
import { FormGroup } from '../../components/form-group'
import { Input } from '../../components/input'
import { Intent } from '../../config/intent'
import { Paths } from '../../config/routes'
import { authService } from '../../lib/auth-service'
import { Validation } from '../../lib/validation'

export const TestId = {
    FORM: 'sign-in-form',
}

export const I18n = {
    HEADING: 'Sign in to your Masa account',
    SUB_HEADING: 'Sign in to access your account',
    EMAIL_LABEL: 'Email address*',
    PASSWORD_LABEL: 'Password*',
    FORGOT_TEXT: 'Forgot your passowrd?',
    FORGOT_LINK: 'Reset now',
    SUBMIT: 'Sign in',
}

export interface SignInFormValues {
    email: string
    password: string
}

export interface SignInFormProps {
    onComplete: (user: CognitoUser) => void
}

export function SignInForm({ onComplete }: SignInFormProps) {
    const [error, setError] = React.useState('')
    const [loading, setLoading] = React.useState(false)
    const [show, setShow] = React.useState(false)
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<SignInFormValues>()

    function handleError(err: any) {
        setError(err.message)
        setLoading(false)
    }

    async function onSubmit(data: SignInFormValues) {
        setLoading(true)

        try {
            const user = await authService.signIn({
                username: data.email,
                password: data.password,
            })

            setLoading(false)
            onComplete(user)
        } catch (err) {
            handleError(err)
        }
    }

    return (
        <React.Fragment>
            <AuthHeading heading={I18n.HEADING} subHeading={I18n.SUB_HEADING} />
            <form onSubmit={handleSubmit(onSubmit)} data-testid={TestId.FORM}>
                <FormGroup
                    label={I18n.EMAIL_LABEL}
                    htmlFor='email'
                    intent={
                        errors.email?.message ? Intent.DANGER : Intent.DEFAULT
                    }
                    message={errors.email?.message}>
                    <Input
                        intent={
                            errors.email?.message
                                ? Intent.DANGER
                                : Intent.DEFAULT
                        }
                        id='email'
                        {...register('email', Validation.email)}
                    />
                </FormGroup>
                <FormGroup
                    label={I18n.PASSWORD_LABEL}
                    htmlFor='password'
                    intent={
                        errors.password?.message
                            ? Intent.DANGER
                            : Intent.DEFAULT
                    }
                    message={errors.password?.message}>
                    <Input
                        type={show ? 'text' : 'password'}
                        intent={
                            errors.password?.message
                                ? Intent.DANGER
                                : Intent.DEFAULT
                        }
                        rightElement={
                            <Button
                                minimal={true}
                                onClick={() => setShow(!show)}>
                                {show ? 'Hide' : 'Show'}
                            </Button>
                        }
                        id='password'
                        {...register('password', Validation.password)}
                    />
                </FormGroup>
                <p className='mb-10'>
                    <span>{I18n.FORGOT_TEXT}</span>{' '}
                    <Link to={Paths.RESET_PASSWORD} className='text-black'>
                        {I18n.FORGOT_LINK}
                    </Link>
                </p>
                <Button
                    loading={loading}
                    disabled={loading}
                    fill={true}
                    type='submit'
                    size='large'
                    className='mb-4'>
                    {I18n.SUBMIT}
                </Button>
                {error && <p className='text-center text-danger'>{error}</p>}
            </form>
        </React.Fragment>
    )
}
